import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';

const ESTADO_BADGE = {
  borrador:  { label: 'Borrador',  bg: '#1A1F1D', color: '#8A9994' },
  enviado:   { label: 'Enviado',   bg: '#0D1E2E', color: '#60A5FA' },
  aceptado:  { label: 'Aceptado', bg: '#12382D', color: '#4CAF50' },
  rechazado: { label: 'Rechazado', bg: '#2D0F0F', color: '#EF4444' },
};

const TIPO_LABEL = {
  reparacion:    'Reparación',
  modernizacion: 'Modernización',
  normativo:     'Normativo',
};

const TRANSICIONES = {
  borrador:  ['enviado'],
  enviado:   ['aceptado', 'rechazado'],
  rechazado: ['borrador'],
};

const CLP = (n) =>
  Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const INPUT_ST = {
  flex: 1, minWidth: 200, padding: '6px 10px',
  borderRadius: 8, fontSize: 16, fontWeight: 700,
  background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
};
const BTN_CANCEL = {
  padding: '6px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
  background: 'transparent', cursor: 'pointer', fontSize: 13,
  color: '#8A9994', fontFamily: 'inherit',
};
const CARD = {
  background: '#161C19', borderRadius: 12, padding: 20, marginBottom: 16,
  border: '1px solid rgba(255,255,255,0.07)',
};

export default function DetallePresupuesto({ id, onVolver }) {
  const { tecnico } = useAuth();
  const esAdmin = ['admin', 'superadmin'].includes(tecnico?.rol);
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [devolviendo, setDevolviendo] = useState(false);
  const [motivoDev, setMotivoDev] = useState('');
  const [guardandoDev, setGuardandoDev] = useState(false);
  const [errorDev, setErrorDev] = useState('');
  const [descargando, setDescargando] = useState(false);
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreInput, setNombreInput] = useState('');
  const [nombreAlerta, setNombreAlerta] = useState('');
  const [guardandoNombre, setGuardandoNombre] = useState(false);
  const [editandoNumero, setEditandoNumero] = useState(false);
  const [numeroInput, setNumeroInput] = useState('');
  const [numeroAlerta, setNumeroAlerta] = useState('');
  const [guardandoNumero, setGuardandoNumero] = useState(false);

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/presupuestos/${id}`);
      setPresupuesto(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar presupuesto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [id]); // eslint-disable-line

  const descargarPDF = async () => {
    setDescargando(true);
    try {
      const token = localStorage.getItem('solvert_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/presupuestos/${id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Error al generar PDF');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${presupuesto?.numero || 'presupuesto'}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 150);
    } catch (e) {
      alert(e.message || 'Error al descargar PDF');
    } finally {
      setDescargando(false);
    }
  };

  const cambiarEstado = async (nuevoEstado) => {
    if (!window.confirm(`¿Cambiar estado a "${nuevoEstado}"?`)) return;
    setCambiandoEstado(true);
    try {
      await api.put(`/presupuestos/${id}/estado`, { estado: nuevoEstado });
      cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al cambiar estado');
    } finally {
      setCambiandoEstado(false);
    }
  };

  const devolverBorrador = async () => {
    if (motivoDev.trim().length < 5) {
      setErrorDev('El motivo es obligatorio (mínimo 5 caracteres)');
      return;
    }
    setGuardandoDev(true);
    setErrorDev('');
    try {
      await api.post(`/presupuestos/${id}/devolver-borrador`, { motivo: motivoDev.trim() });
      setDevolviendo(false);
      setMotivoDev('');
      cargar();
    } catch (e) {
      setErrorDev(e.response?.data?.error || 'Error al devolver a borrador');
    } finally {
      setGuardandoDev(false);
    }
  };

  const abrirRenombrar = () => {
    setNombreInput(p?.nombre || '');
    setNombreAlerta('');
    setEditandoNombre(true);
  };

  const verificarNombre = useCallback(async (val) => {
    if (!val.trim()) { setNombreAlerta(''); return; }
    try {
      const r = await api.get('/presupuestos');
      const coincide = r.data.find(x =>
        x.nombre?.toLowerCase() === val.trim().toLowerCase() && x.id !== id
      );
      setNombreAlerta(coincide ? `Ya existe un presupuesto con ese nombre (${coincide.numero})` : '');
    } catch { setNombreAlerta(''); }
  }, [id]);

  useEffect(() => {
    if (!editandoNombre) return;
    const t = setTimeout(() => verificarNombre(nombreInput), 400);
    return () => clearTimeout(t);
  }, [nombreInput, editandoNombre, verificarNombre]);

  const verificarNumero = useCallback(async (val) => {
    if (!val.trim()) { setNumeroAlerta(''); return; }
    try {
      const r = await api.get('/presupuestos');
      const coincide = r.data.find(x =>
        x.numero?.toLowerCase() === val.trim().toLowerCase() && x.id !== id
      );
      setNumeroAlerta(coincide ? `Ya existe un presupuesto con ese número (${coincide.numero})` : '');
    } catch { setNumeroAlerta(''); }
  }, [id]);

  useEffect(() => {
    if (!editandoNumero) return;
    const t = setTimeout(() => verificarNumero(numeroInput), 400);
    return () => clearTimeout(t);
  }, [numeroInput, editandoNumero, verificarNumero]);

  const guardarNombre = async () => {
    if (!nombreInput.trim()) { setNombreAlerta('El nombre no puede estar vacío'); return; }
    setGuardandoNombre(true);
    try {
      await api.put(`/presupuestos/${id}/nombre`, { nombre: nombreInput.trim() });
      setEditandoNombre(false);
      cargar();
    } catch (e) {
      setNombreAlerta(e.response?.data?.error || 'Error al guardar nombre');
    } finally {
      setGuardandoNombre(false);
    }
  };

  const abrirEditarNumero = () => {
    setNumeroInput(presupuesto?.numero || '');
    setNumeroAlerta('');
    setEditandoNumero(true);
  };

  const guardarNumero = async () => {
    if (!numeroInput.trim()) { setNumeroAlerta('El número no puede estar vacío'); return; }
    setGuardandoNumero(true);
    try {
      await api.put(`/presupuestos/${id}/numero`, { numero: numeroInput.trim() });
      setEditandoNumero(false);
      cargar();
    } catch (e) {
      setNumeroAlerta(e.response?.data?.error || 'Error al guardar número');
    } finally {
      setGuardandoNumero(false);
    }
  };

  if (loading) return <p style={{ color: '#8A9994', textAlign: 'center', padding: 40 }}>Cargando…</p>;
  if (error) return (
    <div style={{ padding: 20 }}>
      <p style={{ color: '#EF4444' }}>{error}</p>
      <button onClick={onVolver} style={{ color: '#4CAF50', background: 'none', border: 'none',
                                          cursor: 'pointer', fontFamily: 'inherit' }}>
        ← Volver
      </button>
    </div>
  );
  if (!presupuesto) return null;

  const p = presupuesto;
  const badge = ESTADO_BADGE[p.estado] || ESTADO_BADGE.borrador;
  const opcionesEstado = TRANSICIONES[p.estado] || [];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={onVolver}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                   padding: '6px 14px', cursor: 'pointer', fontSize: 14,
                   color: '#C9D3CE', fontFamily: 'inherit' }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          {editandoNombre ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input autoFocus value={nombreInput} onChange={e => setNombreInput(e.target.value)}
                style={{ ...INPUT_ST, border: `1.5px solid ${nombreAlerta ? '#F59E0B' : '#0f6e56'}` }} />
              <button onClick={guardarNombre} disabled={guardandoNombre || !!nombreAlerta}
                style={{ padding: '6px 14px', background: '#0f6e56', color: 'white', border: 'none',
                         borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13,
                         fontFamily: 'inherit' }}>
                {guardandoNombre ? 'Guardando…' : 'Guardar'}
              </button>
              <button onClick={() => setEditandoNombre(false)} style={BTN_CANCEL}>Cancelar</button>
              {nombreAlerta && <p style={{ width: '100%', margin: '2px 0 0', fontSize: 12, color: '#F59E0B' }}>{nombreAlerta}</p>}
            </div>
          ) : editandoNumero ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#8A9994', fontWeight: 600 }}>N°</span>
              <input autoFocus value={numeroInput} onChange={e => setNumeroInput(e.target.value.toUpperCase())}
                style={{ ...INPUT_ST, fontSize: 14, fontFamily: 'monospace',
                         border: `1.5px solid ${numeroAlerta ? '#F59E0B' : '#0f6e56'}` }} />
              <button onClick={guardarNumero} disabled={guardandoNumero || !!numeroAlerta}
                style={{ padding: '6px 14px', background: '#0f6e56', color: 'white', border: 'none',
                         borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13,
                         fontFamily: 'inherit' }}>
                {guardandoNumero ? 'Guardando…' : 'Guardar'}
              </button>
              <button onClick={() => setEditandoNumero(false)} style={BTN_CANCEL}>Cancelar</button>
              {numeroAlerta && <p style={{ width: '100%', margin: '2px 0 0', fontSize: 12, color: '#F59E0B' }}>{numeroAlerta}</p>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#F4F7F5' }}>
                {p.nombre || p.numero}
              </h2>
              {p.nombre && (
                <span style={{ fontSize: 12, color: '#6B7C74', fontFamily: 'monospace' }}>{p.numero}</span>
              )}
              <button onClick={abrirRenombrar}
                style={{ padding: '3px 10px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                         background: '#0D1311', cursor: 'pointer', fontSize: 12, color: '#8A9994',
                         fontFamily: 'inherit' }}>
                Renombrar
              </button>
              <button onClick={abrirEditarNumero}
                style={{ padding: '3px 10px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                         background: '#0D1311', cursor: 'pointer', fontSize: 12, color: '#8A9994',
                         fontFamily: 'inherit' }}>
                Editar N°
              </button>
            </div>
          )}
        </div>
        <span style={{ background: badge.bg, color: badge.color, borderRadius: 6,
                       padding: '4px 14px', fontWeight: 700, fontSize: 14 }}>
          {badge.label}
        </span>
        {opcionesEstado.map(op => (
          <button key={op} onClick={() => cambiarEstado(op)} disabled={cambiandoEstado}
            style={{ padding: '7px 16px', border: '1.5px solid #2E4A40', borderRadius: 8,
                     background: '#0D1311', color: '#4CAF50', fontWeight: 600,
                     cursor: cambiandoEstado ? 'wait' : 'pointer', fontSize: 14,
                     fontFamily: 'inherit' }}>
            → {op.charAt(0).toUpperCase() + op.slice(1)}
          </button>
        ))}
        {esAdmin && (p.estado === 'enviado' || p.estado === 'aceptado') && (
          <button onClick={() => { setErrorDev(''); setMotivoDev(''); setDevolviendo(true); }}
            disabled={cambiandoEstado}
            title="Devolver a borrador para corregir (registra el motivo)"
            style={{ padding: '7px 16px', border: '1.5px solid #3D2B00', borderRadius: 8,
                     background: '#1C1200', color: '#F59E0B', fontWeight: 600,
                     cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>
            ↩ Devolver a borrador
          </button>
        )}
        {p.pdf_url ? (
          <a href={p.pdf_url} target="_blank" rel="noopener noreferrer"
            style={{ padding: '7px 16px', background: '#0f6e56', color: 'white',
                     border: 'none', borderRadius: 8, fontWeight: 600,
                     fontSize: 14, textDecoration: 'none', display: 'inline-block' }}>
            Drive PDF
          </a>
        ) : (
          <button onClick={descargarPDF} disabled={descargando}
            style={{ padding: '7px 16px', background: '#0f6e56', color: 'white',
                     border: 'none', borderRadius: 8, fontWeight: 600,
                     cursor: descargando ? 'wait' : 'pointer', fontSize: 14,
                     fontFamily: 'inherit' }}>
            {descargando ? 'Generando…' : 'Descargar PDF'}
          </button>
        )}
      </div>

      {/* Panel devolver a borrador (motivo obligatorio) */}
      {devolviendo && (
        <div style={{ ...CARD, border: '1px solid #3D2B00', background: '#1A1408' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, color: '#F59E0B' }}>
            Devolver a borrador
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#C9D3CE', lineHeight: 1.5 }}>
            El presupuesto volverá a estado <strong>Borrador</strong> para poder editarlo.
            {p.estado === 'aceptado'
              ? ' La orden de trabajo generada al aceptarlo será eliminada.'
              : ''}
            {' '}Indica el motivo (queda registrado en la auditoría).
          </p>
          <textarea autoFocus value={motivoDev} rows={3}
            onChange={e => setMotivoDev(e.target.value)}
            placeholder="Ej: Se marcó por error / el cliente pidió ajustar valores antes de aceptar…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px',
                     border: '1.5px solid #3D2B00', borderRadius: 8, fontSize: 14,
                     background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
                     resize: 'vertical' }} />
          {errorDev && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#EF4444' }}>{errorDev}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={() => { setDevolviendo(false); setErrorDev(''); }} style={BTN_CANCEL}>
              Cancelar
            </button>
            <button onClick={devolverBorrador} disabled={guardandoDev}
              style={{ padding: '7px 18px', background: guardandoDev ? '#7a5a10' : '#B45309',
                       color: 'white', border: 'none', borderRadius: 8, fontWeight: 600,
                       cursor: guardandoDev ? 'wait' : 'pointer', fontSize: 14, fontFamily: 'inherit' }}>
              {guardandoDev ? 'Devolviendo…' : 'Confirmar devolución'}
            </button>
          </div>
        </div>
      )}

      {/* Nota de devolución (si este borrador provino de una devolución) */}
      {p.estado === 'borrador' && p.motivo_devolucion && (
        <div style={{ ...CARD, border: '1px solid #3D2B00', background: '#160F04' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B',
                         textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Devuelto a borrador
            {p.devuelto_at ? ` · ${new Date(p.devuelto_at).toLocaleDateString('es-CL')}` : ''}
          </span>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#C9D3CE', lineHeight: 1.5 }}>
            {p.motivo_devolucion}
          </p>
        </div>
      )}

      {/* Datos generales */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#F4F7F5' }}>Datos generales</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <Dato label="Comunidad" valor={p.comunidad || '—'} />
          <Dato label="Tipo" valor={TIPO_LABEL[p.tipo] || p.tipo} />
          <Dato label="Creado por" valor={p.created_by_nombre || '—'} />
          <Dato label="Fecha creación" valor={new Date(p.created_at).toLocaleDateString('es-CL')} />
          <Dato label="Forma de pago" valor={(() => {
            if (p.tipo_pago !== 'cuotas') return 'Pago completo';
            const pct = parseFloat(p.porcentaje_inicial) || 0;
            return `${pct}% inicial + ${100 - pct}% en ${p.n_cuotas || '?'} cuotas`;
          })()} />
          {p.enviado_at && (
            <Dato label="Enviado" valor={new Date(p.enviado_at).toLocaleDateString('es-CL')} />
          )}
        </div>
        {p.comentario && (
          <div style={{ marginTop: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#9FAAA5', display: 'block', marginBottom: 4 }}>
              Descripción del trabajo
            </span>
            <p style={{ margin: 0, fontSize: 14, color: '#C9D3CE', lineHeight: 1.5 }}>{p.comentario}</p>
          </div>
        )}
      </div>

      {/* Ascensores */}
      {p.ascensores?.length > 0 && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#F4F7F5' }}>
            Ascensores ({p.ascensores.length})
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {p.ascensores.map(a => (
              <span key={a.id} style={{ background: '#12382D', color: '#4CAF50', borderRadius: 20,
                                        padding: '5px 12px', fontSize: 13, fontWeight: 600 }}>
                {a.label} {a.torre ? `(Torre ${a.torre})` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Ítems */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#F4F7F5' }}>
          Ítems ({p.items.length})
        </h3>
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0D1311' }}>
                {['#', 'Descripción', 'Cantidad', 'P.Unit.', 'Dto%', 'Subtotal'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: h === '#' ? 'center' : 'left',
                                      fontWeight: 600, color: '#9FAAA5',
                                      borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {p.items.map((it, idx) => (
                <React.Fragment key={it.id}>
                  <tr style={{ background: idx % 2 === 0 ? '#111714' : '#0D1311',
                               borderBottom: it.comentario ? 'none' : '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '9px 12px', textAlign: 'center', color: '#6B7C74' }}>{idx + 1}</td>
                    <td style={{ padding: '9px 12px', color: '#F4F7F5' }}>
                      {it.descripcion}
                      {it.catalogo_codigo && (
                        <span style={{ fontSize: 11, color: '#6B7C74', marginLeft: 6, fontFamily: 'monospace' }}>
                          {it.catalogo_codigo}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#C9D3CE' }}>{it.cantidad}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#C9D3CE' }}>{CLP(it.precio_unitario)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#C9D3CE' }}>
                      {it.descuento_linea > 0 ? `${it.descuento_linea}%` : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#F4F7F5' }}>
                      {CLP(it.subtotal)}
                    </td>
                  </tr>
                  {it.comentario && (
                    <tr style={{ background: idx % 2 === 0 ? '#111714' : '#0D1311',
                                 borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td></td>
                      <td colSpan={5} style={{ padding: '2px 12px 10px', fontSize: 12, color: '#8A9994',
                                               fontStyle: 'italic' }}>
                        {it.comentario}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen montos */}
      <div style={{ ...CARD, maxWidth: 380, marginLeft: 'auto' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#F4F7F5' }}>Resumen</h3>
        <FilaMonto label="Subtotal" valor={CLP(p.subtotal)} />
        {parseFloat(p.descuento) > 0 && (
          <FilaMonto label="Descuento" valor={`-${CLP(p.descuento)}`} color="#EF4444" />
        )}
        <FilaMonto label="IVA" valor={CLP(p.iva)} />
        <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '10px 0' }} />
        <FilaMonto label="Total" valor={CLP(p.total)} bold size={18} color="#4CAF50" />
      </div>

      {/* Notas y condiciones */}
      {(p.notas || p.condiciones_pago || p.condiciones_tecnicas) && (
        <div style={{ ...CARD, marginBottom: 24 }}>
          {p.notas && (
            <div style={{ marginBottom: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#9FAAA5', display: 'block', marginBottom: 4 }}>
                Notas
              </span>
              <p style={{ margin: 0, fontSize: 14, color: '#C9D3CE', lineHeight: 1.5 }}>{p.notas}</p>
            </div>
          )}
          {p.condiciones_pago && (
            <div style={{ marginBottom: p.condiciones_tecnicas ? 14 : 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#9FAAA5', display: 'block', marginBottom: 4 }}>
                Condiciones de Pago
              </span>
              <p style={{ margin: 0, fontSize: 14, color: '#C9D3CE', lineHeight: 1.5 }}>{p.condiciones_pago}</p>
            </div>
          )}
          {p.condiciones_tecnicas && (
            <div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#9FAAA5', display: 'block', marginBottom: 4 }}>
                Condiciones Técnicas
              </span>
              <p style={{ margin: 0, fontSize: 14, color: '#C9D3CE', lineHeight: 1.5 }}>{p.condiciones_tecnicas}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Dato({ label, valor }) {
  return (
    <div>
      <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7C74',
                     textTransform: 'uppercase', marginBottom: 2 }}>{label}</span>
      <span style={{ fontSize: 14, color: '#F4F7F5' }}>{valor}</span>
    </div>
  );
}

function FilaMonto({ label, valor, bold, size, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: size || 14, color: '#8A9994' }}>{label}</span>
      <span style={{ fontSize: size || 14, fontWeight: bold ? 700 : 400, color: color || '#F4F7F5' }}>
        {valor}
      </span>
    </div>
  );
}
